import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import { useAuth } from '../../hooks/useAuth';
import { logger } from '../../utils/logger';
import VAQuickActionsNav from '../../components/navigation/VAQuickActionsNav';

// Import onboarding component (rendered globally in Routes)

// Import dashboard components
import MetricsCard from './components/MetricsCard';
import MetricsCardSkeleton from './components/MetricsCardSkeleton';
import JobCard from '../../components/jobs/JobCard';
import JobCardSkeleton from '../../components/jobs/JobCardSkeleton';
import SkillsAssessmentPrompt from './components/SkillsAssessmentPrompt';
import EnhancedActivityFeed from './components/EnhancedActivityFeed';
import JobSearchInsights from './components/JobSearchInsights';
import ProfileCompletionCard from './components/ProfileCompletionCard';
import FeedbackForm from '../../components/feedback/FeedbackForm';

// Import job matching service
import { getJobRecommendations, refreshJobRecommendations } from '../../services/jobMatchingService';
import { getUserDashboardMetrics } from '../../services/dashboardService';
import { useDebounceCallback } from '../../hooks/useDebounce';
import activityService from '../../services/activityService';

const VADashboard = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [pullStartY, setPullStartY] = useState(0);
  const [pullMoveY, setPullMoveY] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  const [profileCompletionPercentage, setProfileCompletionPercentage] = useState(0);
  const [showFeedbackForm, setShowFeedbackForm] = useState(false);
  const [showIncompleteBanner, setShowIncompleteBanner] = useState(false);
  const navigate = useNavigate();
  const { user: currentUser, userProfile } = useAuth();

  // Auto-redirect brand-new VAs to the onboarding wizard
  useEffect(() => {
    if (!userProfile) return;
    const started = localStorage.getItem(`va_started_profile_${userProfile.id}`);
    const isBrandNew = !userProfile.title && !userProfile.bio && !userProfile.hourly_rate;
    if (!started && isBrandNew) {
      navigate('/va-onboarding', { replace: true });
      return;
    }
    // Show incomplete profile banner for VAs who started onboarding but haven't finished
    if (started) {
      const dismissed = localStorage.getItem(`va_banner_dismissed_${userProfile.id}`);
      const isIncomplete =
        !userProfile.title ||
        !userProfile.bio ||
        !userProfile.hourly_rate ||
        !Array.isArray(userProfile.skills) ||
        userProfile.skills.length === 0;
      if (!dismissed && isIncomplete) {
        setShowIncompleteBanner(true);
      }
    }
  }, [userProfile, navigate]);

  const handleDismissBanner = () => {
    if (userProfile?.id) {
      localStorage.setItem(`va_banner_dismissed_${userProfile.id}`, 'true');
    }
    setShowIncompleteBanner(false);
  };
  
  // Mobile performance optimization
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  // Notification permission state (browser Notification API)
  const [notificationsEnabled, setNotificationsEnabled] = useState(() => 
    typeof window !== 'undefined' && 'Notification' in window && window.Notification.permission === 'granted'
  );

  const handleRequestNotificationPermission = useCallback(async () => {
    if (!window.Notification) return;
    const permission = await window.Notification.requestPermission();
    setNotificationsEnabled(permission === 'granted');
  }, []);
  // Dashboard metrics state
  const [metricsData, setMetricsData] = useState([
    {
      title: "Active Applications",
      value: "0",
      subtitle: "0 pending responses",
      icon: "Send",
      trend: "neutral",
      trendValue: "0",
      color: "primary"
    },
    {
      title: "Profile Views",
      value: "0",
      subtitle: "This week",
      icon: "Eye",
      trend: "neutral",
      trendValue: "0%",
      color: "success"
    },
    {
      title: "Match Score",
      value: "0%",
      subtitle: "Not enough data",
      icon: "Target",
      trend: "neutral",
      trendValue: "0%",
      color: "accent"
    },
    {
      title: "Response Rate",
      value: "0%",
      subtitle: "Last 30 days",
      icon: "MessageCircle",
      trend: "neutral",
      trendValue: "0%",
      color: "warning"
    }
  ]);

  // Mock job recommendations data
  const [recommendedJobs, setRecommendedJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);

  // Debounced function to refresh job recommendations (enhanced for up-skills)
  const debouncedRefreshJobs = useDebounceCallback(async () => {
    if (currentUser?.id) {
      try {
        setJobsLoading(true);
        logger.debug('Refreshing job recommendations for user', { userId: currentUser.id });
        
        // Use the enhanced refresh function which handles up-skills format detection
        const result = await refreshJobRecommendations(currentUser.id);
        if (result.success) {
          logger.debug('Job recommendations refreshed successfully', { 
            upSkillsFormat: result.upSkillsFormat ? 'up-skills format' : 'legacy format'
          });
          
          const jobs = await getJobRecommendations(currentUser.id);
          setRecommendedJobs(jobs);
          
          // Log improved matching for up-skills users
          if (result.upSkillsFormat) {
            logger.debug('Enhanced skill matching applied for up-skills user');
          }
        } else {
          logger.error('Failed to refresh job recommendations', result.error);
        }
      } catch (error) {
        logger.error('Failed to load job recommendations', error);
      } finally {
        setJobsLoading(false);
      }
    }
  }, 1000); // 1 second debounce

  useEffect(() => {
    const fetchRecommendedJobs = async () => {
      if (currentUser?.id) {
        try {
          logger.debug('Fetching job recommendations for user', { userId: currentUser.id });
          setJobsLoading(true);
          
          // Check if user has up-skills format for better logging
          const hasUpSkills = Array.isArray(userProfile?.skills) && userProfile.skills.some(skill => 
            typeof skill === 'object' && skill !== null && 
            (skill.rating !== undefined || skill.upSkillsFormat === true)
          );
          
          if (hasUpSkills) {
            logger.debug('User has up-skills format - using enhanced matching algorithm');
          }
          
          const jobs = await getJobRecommendations(currentUser.id);
          logger.debug('Received job recommendations', { count: jobs.length });
          setRecommendedJobs(jobs);
        } catch (error) {
          logger.error('Failed to load job recommendations', error);
        } finally {
          setJobsLoading(false);
        }
      } else {
        logger.warn('No current user ID available for job recommendations');
      }
    };

    fetchRecommendedJobs();
  }, [currentUser, userProfile?.skills]);

  // Listen for profile updates and refresh recommendations
  useEffect(() => {
    const handleProfileUpdate = () => {
      // Profile updated, refreshing job recommendations
      logger.debug('Profile updated, refreshing job recommendations');
      debouncedRefreshJobs();
    };

    // Listen for custom profile update events
    window.addEventListener('profileUpdated', handleProfileUpdate);
    
    return () => {
      window.removeEventListener('profileUpdated', handleProfileUpdate);
    };
  }, [debouncedRefreshJobs]);



  // Mock skills assessment data - TODO: Replace with real data from API
  const [skillsAssessments, setSkillsAssessments] = useState([]);

  // Load skills assessments
  useEffect(() => {
    const fetchSkillsAssessments = async () => {
      if (currentUser?.id) {
        try {
          // This would be replaced with a real API call
          // const result = await assessmentService.getUserAssessments(currentUser.id);
          // setSkillsAssessments(result.data);
          
          // For now, just set an empty array since we're removing mock data
          setSkillsAssessments([]);
        } catch (error) {
          logger.error('Failed to load skills assessments', error);
          setSkillsAssessments([]);
        }
      }
    };
    
    fetchSkillsAssessments();
  }, [currentUser]);

  // User activities state
  const [recentActivities, setRecentActivities] = useState([]);
  const [activitiesLoading, setActivitiesLoading] = useState(true);

  // Fetch user activities
  const fetchUserActivities = useCallback(async () => {
    if (currentUser?.id) {
      try {
        setActivitiesLoading(true);
        
        // Check if VA activities table exists
        const tableExists = await activityService.checkTableExists('va_activities');
        
        if (tableExists) {
          // Get real activities from the database
          const result = await activityService.getRecentActivities(currentUser.id);
          
          if (result.success) {
            setRecentActivities(result.data);
          } else {
            // No activities or error
            setRecentActivities([]);
            logger.warn('No activities found or error retrieving activities', result.error);
          }
        } else {
          // No activities table found
          setRecentActivities([]);
          logger.warn('VA activities table does not exist yet');
        }
      } catch (error) {
        logger.error('Failed to load user activities', error);
        // Fallback to empty array in case of error
        setRecentActivities([]);
      } finally {
        setActivitiesLoading(false);
      }
    }
  }, [currentUser]);

  // Handle profile completion
  const handleCompleteSection = () => {
    navigate('/va-profile-settings');
  };
  
  // Track profile completion percentage
  const handleProfileCompletionUpdate = (percentage) => {
    setProfileCompletionPercentage(percentage);
  };

  // Fetch dashboard metrics
  const fetchDashboardMetrics = useCallback(async () => {
    if (currentUser?.id) {
      try {
        setIsLoading(true);
        const metrics = await getUserDashboardMetrics(currentUser.id);
        setMetricsData(metrics);
      } catch (error) {
        logger.error('Failed to load dashboard metrics', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [currentUser]);

  // Load dashboard metrics on component mount or when user changes
  useEffect(() => {
    fetchDashboardMetrics();
  }, [currentUser, fetchDashboardMetrics]);

  // Load activities when component mounts or user changes
  useEffect(() => {
    if (currentUser?.id) {
      fetchUserActivities();
    }
  }, [currentUser, fetchUserActivities]);

  // Handle pull-to-refresh
  const handleRefresh = async () => {
    setRefreshing(true);
    // Refresh metrics, job recommendations and activities
    await Promise.all([
      fetchDashboardMetrics(),
      debouncedRefreshJobs(),
      fetchUserActivities()
    ]);
    setRefreshing(false);
  };

  // Handle touch events for pull-to-refresh
  const handleTouchStart = (e) => {
    // Only enable pull-to-refresh at the top of the page
    if (window.scrollY === 0) {
      setPullStartY(e.touches[0].clientY);
      setIsPulling(true);
    }
  };

  const handleTouchMove = (e) => {
    if (!isPulling) return;
    
    const touchY = e.touches[0].clientY;
    setPullMoveY(touchY);
    
    // Calculate pull distance
    const pullDistance = touchY - pullStartY;
    
    // If pulled down more than 60px and not already refreshing, trigger refresh
    if (pullDistance > 60 && !refreshing && window.scrollY === 0) {
      handleRefresh();
      setIsPulling(false);
    }
  };

  const handleTouchEnd = () => {
    setIsPulling(false);
  };

  // Handle job actions
  const handleJobApply = (job) => {
    // Navigate to job details application page with jobId parameter
    navigate(`/job-details?jobId=${job.id}&quickApply=true`);
  };

  const handleJobViewDetails = (job) => {
    // Navigate to job details page using legacy UUID route
    navigate(`/job/${job.id}`);
  };

  const handleJobBookmark = (_job) => {
    // TODO: Implement bookmark logic - toggle bookmark status
    // In real app, this would call API to update bookmark status
  };

  // Handle skills assessment
  const handleTakeAssessment = () => {
    navigate('/skills-assessment');
  };

  const handleViewAllAssessments = () => {
    navigate('/skills-assessment');
  };

  // Handle activity timeline
  const handleViewAllActivities = () => {
    // Navigate to the activities page
    navigate('/activities');
  };

  /* 
  // Commenting out unused function
  // Handle quick actions
  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'search-jobs': 
        navigate('/job-search');
        break;
      case 'update-profile': 
        navigate('/va-profile');
        break;
      case 'take-assessment': 
        navigate('/skills-assessment');
        break;
      case 'view-messages': 
        navigate('/messages');
        break;
      case 'view-interviews':
        navigate('/interviews');
        break;
      case 'schedule-interview':
        navigate('/interviews');
        break;
      default:
        logger.debug('Unknown action', { actionId });
    }
  };
  */

  // Feedback form handlers
  const handleFeedbackSubmit = (feedbackData) => {
    logger.info('Feedback submitted', feedbackData);
    setShowFeedbackForm(false);
    // Optionally show a success message
    alert('Thank you for your feedback!');
  };

  const handleFeedbackCancel = () => {
    setShowFeedbackForm(false);
  };

  return (
    <div 
      className="min-h-screen bg-background" 
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
  {/* Onboarding Guide is now rendered globally in Routes */}
      
      <main className="w-full max-w-full sm:max-w-7xl mx-auto px-4 sm:px-4 md:px-6 lg:px-8">
        <div className="pt-3 sm:pt-4 md:pt-6 lg:pt-8 pb-10 sm:pb-24 md:pb-8">
          {/* Mobile Interactive Hints */}
          <div className="md:hidden mb-2 text-center">
            <div className="inline-flex items-center px-3 py-1 bg-primary/5 rounded-full">
              <div className="w-1 h-1 bg-primary/40 rounded-full mr-1.5"></div>
              <span className="text-xs text-primary/70 font-medium">Pull down to refresh</span>
            </div>
          </div>
          
          {/* Mobile Pull-to-Refresh Indicator */}
          {refreshing && (
            <div className="flex justify-center py-4 md:hidden">
              <Icon name="RefreshCw" size={20} className="animate-spin text-primary" />
              <span className="ml-2 text-sm text-primary font-medium">Refreshing...</span>
            </div>
          )}

          {/* Incomplete Profile Banner */}
          {showIncompleteBanner && (
            <div className="mb-4 flex items-start sm:items-center justify-between gap-3 bg-amber-50 border border-amber-200 rounded-xl p-4">
              <div className="flex items-start sm:items-center gap-3">
                <div className="flex-shrink-0 w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <Icon name="AlertCircle" size={18} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-amber-900">Your profile is incomplete</p>
                  <p className="text-xs text-amber-700 mt-0.5">
                    Add your title, bio, skills, and hourly rate to start getting discovered by employers.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => navigate('/va-profile-settings')}
                  className="text-xs font-semibold text-amber-800 bg-amber-100 hover:bg-amber-200 px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                >
                  Complete Profile
                </button>
                <button
                  onClick={handleDismissBanner}
                  aria-label="Dismiss banner"
                  className="text-amber-500 hover:text-amber-700 transition-colors p-1"
                >
                  <Icon name="X" size={16} />
                </button>
              </div>
            </div>
          )}

          {/* Welcome Section - Mobile First */}
          <div id="welcome-section" className="mb-3 sm:mb-4 md:mb-6">
            <div className="space-y-3 sm:space-y-0 sm:flex sm:items-start sm:justify-between sm:gap-4">
              <div className="flex-1 min-w-0">
                <div className="space-y-1.5 sm:space-y-2">
                  <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-text-primary leading-tight">
                    <span className="block sm:inline">Welcome back,</span>{' '}
                    <span className="block sm:inline text-primary">
                      {userProfile?.full_name?.split(' ')[0] || 'Virtual Assistant'}!
                    </span>
                  </h1>
                  <p className="text-xs sm:text-sm md:text-base text-text-secondary">
                    Here's what's happening with your VA career
                  </p>
                </div>
                
                {/* Notification permission prompt */}
                {!notificationsEnabled && (
                  <div className="flex flex-wrap items-center mt-3 gap-2">
                    <button 
                      className="flex items-center text-xs px-2.5 py-1.5 rounded-full bg-primary/10 text-primary hover:bg-primary/20 active:bg-primary/30 whitespace-nowrap font-medium shadow-sm"
                      onClick={() => window.Notification && window.Notification.requestPermission()}
                    >
                      <Icon name="Bell" size={12} className="mr-1" />
                      <span className="hidden sm:inline">Enable notifications</span>
                      <span className="sm:hidden">Notify</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Quick Navigation - Mobile Optimized */}
          <div className="mb-3 sm:mb-4 md:mb-6 -mx-1 sm:mx-0">
            <VAQuickActionsNav activeSection="dashboard" />
          </div>

          {/* Tablet and Desktop Layout - Responsive Grid */}
          <div className="hidden md:grid md:grid-cols-4 lg:grid-cols-12 xl:grid-cols-12 gap-3 md:gap-4 lg:gap-6">
            {/* Left Sidebar - Responsive Columns */}
            <div className="md:col-span-4 lg:col-span-3 xl:col-span-3 space-y-3 md:space-y-4 lg:space-y-6">
              {/* Profile Completion Card - Only shown when not at 100% */}
              {(profileCompletionPercentage < 100) && (
                <div id="profile-completion" className="lg:top-4">
                  <ProfileCompletionCard 
                    onCompleteSection={handleCompleteSection}
                    onProfileCompletionUpdate={handleProfileCompletionUpdate}
                  />
                </div>
              )}
              
              {/* Job Search Insights - Left Sidebar */}
              <div className="lg:top-4 lg:mt-6">
                <JobSearchInsights />
              </div>

              {/* Feedback Section - Below Job Insights */}
              <div className="bg-white rounded-lg border border-gray-200 p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-semibold text-gray-800">Share Your Feedback</h3>
                  <Icon name="MessageCircle" size={18} className="text-primary" />
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Help us improve your experience on VA Sprouts
                </p>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => setShowFeedbackForm(true)}
                  className="w-full"
                  iconName="Send"
                >
                  Give Feedback
                </Button>
              </div>
            </div>

            {/* Main Content - Responsive Columns */}
            <div className="md:col-span-4 lg:col-span-6 xl:col-span-6 space-y-3 md:space-y-4 lg:space-y-6">
              {/* Metrics Cards - Desktop */}
              <div className="space-y-3 lg:space-y-4">
                <div id="metrics-section" className="grid grid-cols-2 gap-3 lg:gap-4">
                  {isLoading ? (
                    // Loading placeholder for metrics
                    Array(4).fill().map((_, index) => (
                      <MetricsCardSkeleton key={index} delay={index * 100} />
                    ))
                  ) : (
                    metricsData.map((metric, index) => (
                      <MetricsCard key={index} {...metric} />
                    ))
                  )}
                </div>
              </div>

              {/* Job Recommendations */}
              <div id="recommended-jobs" className="mb-6 lg:mb-8">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold">Recommended Jobs For You</h2>
                  {Array.isArray(userProfile?.skills) && userProfile.skills.some(skill => 
                    typeof skill === 'object' && skill !== null && 
                    (skill.rating !== undefined || skill.upSkillsFormat === true)
                  ) && (
                    <div className="flex items-center text-sm text-primary bg-primary/10 px-2 py-1 rounded-lg">
                      <Icon name="Star" size={14} className="mr-1" />
                      Enhanced Matching
                    </div>
                  )}
                </div>
                {jobsLoading ? (
                  <div className="grid grid-cols-1 gap-4">
                    {Array(4).fill().map((_, index) => (
                      <JobCardSkeleton key={index} delay={index * 150} />
                    ))}
                  </div>
                ) : recommendedJobs.length > 0 ? (
                  <div className="grid grid-cols-1 gap-4">
                    {recommendedJobs.slice(0, 4).map(job => (
                      <JobCard 
                        key={job.id} 
                        job={job} 
                        matchScore={job.matchScore}
                        onApply={handleJobApply}
                        onViewDetails={handleJobViewDetails}
                        onBookmark={handleJobBookmark}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <Icon name="Search" size={48} className="mx-auto text-gray-400 mb-4" />
                    <p className="text-gray-500 mb-2">No recommended jobs found.</p>
                    <p className="text-sm text-gray-400">
                      {Array.isArray(userProfile?.skills) && userProfile.skills.length ? 
                        'Try updating your skills or expanding your preferences to get better matches (at least 20% match score).' :
                        'Complete your profile to get personalized job recommendations.'
                      }
                    </p>
                  </div>
                )}
                {recommendedJobs.length > 4 && (
                  <div className="mt-4 text-center">
                    <Button variant="secondary" onClick={() => navigate('/job-search')}>
                      View All Recommended Jobs
                    </Button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Sidebar - Responsive Layout */}
            <div className="md:col-span-4 lg:col-span-3 xl:col-span-3 md:mt-3 lg:mt-0 space-y-3 md:space-y-4 lg:space-y-6">
              
              {/* Activity Feed - Desktop */}
              <div className="lg:sticky lg:top-20">
                {activitiesLoading ? (
                  <div className="bg-card rounded-xl p-3 md:p-4 shadow-sm animate-pulse">
                    <div className="h-5 md:h-6 w-32 md:w-40 bg-skeleton rounded mb-3 md:mb-4"></div>
                    <div className="space-y-2 md:space-y-3">
                      {Array(3).fill().map((_, index) => ( // Show 3 on tablet, more on desktop
                        <div key={index} className="flex">
                          <div className="h-8 w-8 md:h-10 md:w-10 rounded-full bg-skeleton mr-2 md:mr-3"></div>
                          <div className="flex-1">
                            <div className="h-3 md:h-4 w-3/4 bg-skeleton rounded mb-1 md:mb-2"></div>
                            <div className="h-2 md:h-3 w-1/2 bg-skeleton rounded"></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <EnhancedActivityFeed
                    onViewAll={handleViewAllActivities}
                  />
                )}
              </div>
              
              {/* Skills Assessment - Desktop */}
              {/* <div id="skills-assessment" className="lg:sticky lg:top-4 lg:mt-6">
                <SkillsAssessmentPrompt
                  assessments={skillsAssessments}
                  onTakeAssessment={handleTakeAssessment}
                  onViewAll={handleViewAllAssessments}
                />
              </div> */}
            </div>
          </div>

          {/* Mobile-First Layout */}
          <div className="md:hidden space-y-3 sm:space-y-4">
            {/* Metrics Cards - Mobile First Design */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-base sm:text-lg font-semibold text-text-primary">Your Performance</h2>
                <div className="flex items-center gap-2">
                  <button 
                    onClick={handleRefresh}
                    className="p-1.5 text-gray-400 hover:text-primary active:text-primary-600"
                    title="Refresh dashboard"
                  >
                    <Icon name="RefreshCw" size={14} className={isLoading ? "animate-spin" : ""} />
                  </button>
                  <button className="text-xs text-primary hover:text-primary-600 active:text-primary-700 font-medium px-2 py-1 rounded-md active:bg-primary/10 transition-all">
                    Details
                  </button>
                </div>
              </div>
              <div id="metrics-section-mobile" className="grid grid-cols-2 gap-2.5 sm:gap-3">
                {isLoading ? (
                  // Loading placeholder for metrics (mobile)
                  Array(4).fill().map((_, index) => (
                    <MetricsCardSkeleton key={index} delay={index * 100} />
                  ))
                ) : (
                  metricsData.map((metric, index) => (
                    <MetricsCard key={index} {...metric} />
                  ))
                )}
              </div>
            </div>

            {/* Profile Completion - Mobile Enhanced - Only shown when not at 100% */}
            {(profileCompletionPercentage < 100) && (
              <div id="profile-completion-mobile" className="px-1 transform transition-transform duration-200 active:scale-[0.98]">
                <ProfileCompletionCard 
                  onCompleteSection={handleCompleteSection}
                  onProfileCompletionUpdate={handleProfileCompletionUpdate}
                />
              </div>
            )}

            {/* Job Recommendations - Mobile Enhanced */}
            <div id="recommended-jobs-mobile" className="space-y-3 sm:space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-base sm:text-lg lg:text-xl font-bold text-text-primary line-clamp-2">
                    Jobs For You
                  </h2>
                  <p className="text-xs sm:text-sm text-text-secondary mt-0.5">
                    Personalized recommendations
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {Array.isArray(userProfile?.skills) && userProfile.skills.some(skill => 
                    typeof skill === 'object' && skill !== null && 
                    (skill.rating !== undefined || skill.upSkillsFormat === true)
                  ) && (
                    <div className="flex items-center text-xs text-primary bg-primary/10 px-2 py-1 rounded-full">
                      <Icon name="Star" size={10} className="mr-1" />
                      <span className="hidden sm:inline">Enhanced</span>
                      <span className="sm:hidden">AI</span>
                    </div>
                  )}
                  <button className="text-xs text-primary hover:text-primary-600 font-medium whitespace-nowrap">
                    View All
                  </button>
                </div>
              </div>
              {jobsLoading ? (
                <div className="space-y-2.5 sm:space-y-3">
                  {Array(3).fill().map((_, index) => ( // Show 3 on mobile instead of 4
                    <div key={index} className="animate-pulse">
                      <JobCardSkeleton delay={index * 150} />
                    </div>
                  ))}
                </div>
              ) : recommendedJobs.length > 0 ? (
                <div className="space-y-2.5 sm:space-y-3">
                  {recommendedJobs.slice(0, 3).map(job => ( // Show 3 on mobile for better UX
                    <div 
                      key={job.id}
                      className="transform transition-transform duration-200 hover:scale-[1.02] active:scale-[0.98]"
                    >
                      <JobCard 
                        job={job} 
                        matchScore={job.matchScore}
                        onApply={handleJobApply}
                        onViewDetails={handleJobViewDetails}
                        onBookmark={handleJobBookmark}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 sm:py-8 px-4 bg-gray-50 rounded-xl">
                  <div className="max-w-sm mx-auto">
                    <Icon name="Search" size={40} className="sm:w-12 sm:h-12 mx-auto text-gray-400 mb-3 sm:mb-4" />
                    <h3 className="text-sm sm:text-base font-medium text-gray-700 mb-2">No jobs yet!</h3>
                    <p className="text-xs sm:text-sm text-gray-500 mb-4">
                      {Array.isArray(userProfile?.skills) && userProfile.skills.length ? 
                        'We\'re finding the perfect matches for you.' :
                        'Complete your profile to get amazing job recommendations.'
                      }
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(Array.isArray(userProfile?.skills) && userProfile.skills.length ? '/job-search' : '/va-profile-settings')}
                      className="text-xs sm:text-sm"
                    >
                      {Array.isArray(userProfile?.skills) && userProfile.skills.length ? 
                        'Browse Jobs' : 'Complete Profile'
                      }
                    </Button>
                  </div>
                </div>
              )}
              {recommendedJobs.length > 3 && ( // Changed from 4 to 3 since we show 3 on mobile
                <div className="mt-3 sm:mt-4 text-center">
                  <Button 
                    variant="outline" 
                    onClick={() => navigate('/job-search')}
                    className="w-full sm:w-auto text-sm font-medium"
                  >
                    <Icon name="ArrowRight" size={14} className="mr-1.5" />
                    <span className="hidden sm:inline">View All Recommended Jobs</span>
                    <span className="sm:hidden">View All Jobs ({recommendedJobs.length})</span>
                  </Button>
                </div>
              )}
            </div>

            {/* Job Search Insights - Mobile Enhanced */}
            <div className="px-1 space-y-3">
              <div className="flex items-center justify-between">
                <h2 className="text-base font-semibold text-text-primary">Market Insights</h2>
                <button className="text-xs text-primary hover:text-primary-600 font-medium">
                  View More
                </button>
              </div>
              <JobSearchInsights />
            </div>

            {/* Recent Activity - Mobile Enhanced */}
            <div className="space-y-3">
              {activitiesLoading ? (
                <div className="bg-card rounded-xl p-3 sm:p-4 shadow-sm animate-pulse">
                  <div className="space-y-2 sm:space-y-3">
                    {Array(3).fill().map((_, index) => ( // Show 3 instead of 4 on mobile
                      <div key={index} className="flex">
                        <div className="h-8 w-8 sm:h-10 sm:w-10 rounded-full bg-skeleton mr-2 sm:mr-3"></div>
                        <div className="flex-1">
                          <div className="h-3 sm:h-4 w-3/4 bg-skeleton rounded mb-1 sm:mb-2"></div>
                          <div className="h-2 sm:h-3 w-1/2 bg-skeleton rounded"></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="px-1">
                  <EnhancedActivityFeed
                    onViewAll={handleViewAllActivities}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Feedback Form Modal */}
      {showFeedbackForm && (
        <FeedbackForm
          type="general"
          contextId={null}
          contextTitle="VA Dashboard"
          onSubmit={handleFeedbackSubmit}
          onCancel={handleFeedbackCancel}
        />
      )}
    </div>
  );
};

export default VADashboard;

